import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ChevronLeft } from 'lucide-react';
import Pagination from './components/Pagination';
import { useAuth } from '../../../AuthProvider';
import { sourceChipProps, vintageText, provenanceTooltip } from './provenance';
import { getCellDetail } from './cellDetail';

const clickableCell = { cursor: 'pointer', '&:hover': { backgroundColor: '#f3f7f8' } };

/** Best-effort human-readable summary of a tool call's args - falls back to
 * the raw JSON if nothing recognizable is found. */
function summarizeArgs(args) {
  if (!args || typeof args !== 'object' || Object.keys(args).length === 0) {
    return '—';
  }
  const preferred =
    args.query || args.data_query || args.address || args.item_id || null;
  if (preferred) return String(preferred);
  return JSON.stringify(args);
}

// Live-ish feed: re-poll while parked on the newest page so new activity
// shows up without a manual refresh. Only page 1 auto-refreshes so it never
// yanks someone mid-review off an older page.
const AUTO_REFRESH_MS = 20000;

export default function McpToolCallLog({ returnToHome }) {
  const { getAccessToken } = useAuth();
  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [loading, setLoading] = useState(true);
  // Snapshot of the clicked cell, so the 20s auto-refresh can't change it under the reader.
  const [detail, setDetail] = useState(null);
  const [copied, setCopied] = useState(false);

  const openDetail = (log, column) => {
    setCopied(false);
    setDetail(getCellDetail(log, column));
  };

  const copyDetail = async () => {
    try {
      await navigator.clipboard.writeText(detail.text);
      setCopied(true);
    } catch (e) {
      setCopied(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [page]);

  useEffect(() => {
    if (page !== 1) return undefined;
    const interval = setInterval(() => fetchLogs({ silent: true }), AUTO_REFRESH_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  async function fetchLogs({ silent } = {}) {
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        pageSize: 50,
        search,
      });
      const response = await fetch(
        `${process.env.REACT_APP_NODE_URL}/admin/mcp-tool-calls?${params.toString()}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAccessToken()}`,
          },
        },
      );
      if (!response.ok) throw new Error('Failed to fetch tool call logs');
      const data = await response.json();
      setLogs(data.logs || []);
      setTotalPages(data.totalPages || 1);
      setTotalLogs(data.totalLogs || 0);
    } catch (error) {
      console.error('Error fetching tool call logs:', error);
    } finally {
      setLoading(false);
    }
  }

  function handleSearchSubmit(e) {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  }

  function formatDate(dateStr) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  const tableCellStyle = {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{ padding: '2rem', paddingBottom: '1rem' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Button
            onClick={returnToHome}
            sx={{
              minWidth: 'auto',
              padding: '0.5rem',
              color: '#39585c',
            }}
          >
            <ChevronLeft size={20} />
          </Button>
          <Typography
            sx={{
              color: '#39585c',
              fontSize: '1.5rem',
              fontWeight: '700',
            }}
          >
            MCP Usage Activity
          </Typography>
          <Typography
            sx={{
              color: '#888',
              fontSize: '0.9rem',
            }}
          >
            {totalLogs} call{totalLogs !== 1 ? 's' : ''} logged
          </Typography>
        </Box>
      </Box>

      <form
        onSubmit={handleSearchSubmit}
        style={{
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <TextField
          label='Search by email, tool, prompt, or data source'
          size='small'
          style={{ marginRight: '10px', width: '300px' }}
          InputLabelProps={{ shrink: true }}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <Button
          variant='contained'
          type='submit'
          sx={{
            backgroundColor: '#39585c',
            '&:hover': { backgroundColor: '#39585c' },
          }}
        >
          Search
        </Button>
      </form>

      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#39585c' }}>
              <TableCell sx={tableCellStyle}>Time</TableCell>
              <TableCell sx={tableCellStyle}>Email</TableCell>
              <TableCell sx={tableCellStyle}>Tool</TableCell>
              <TableCell sx={tableCellStyle}>Source</TableCell>
              <TableCell sx={tableCellStyle}>Vintage</TableCell>
              <TableCell sx={tableCellStyle}>Prompt</TableCell>
              <TableCell sx={tableCellStyle}>Args</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 10 }, (_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 7 }, (_, j) => (
                    <TableCell key={j}>
                      <Skeleton />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align='center'>
                  No tool calls logged yet
                </TableCell>
              </TableRow>
            ) : (
              logs.map(log => (
                <TableRow key={log._id}>
                  <TableCell
                    sx={{ ...clickableCell, fontSize: '0.8rem', whiteSpace: 'nowrap' }}
                    onClick={() => openDetail(log, 'time')}
                  >
                    {formatDate(log.createdAt)}
                  </TableCell>
                  <TableCell
                    sx={{ ...clickableCell, fontSize: '0.85rem' }}
                    onClick={() => openDetail(log, 'email')}
                  >
                    {log.email}
                  </TableCell>
                  <TableCell
                    sx={{ ...clickableCell, fontFamily: 'monospace', fontSize: '0.8rem' }}
                    onClick={() => openDetail(log, 'tool')}
                  >
                    {log.toolName}
                  </TableCell>
                  <TableCell
                    sx={{ ...clickableCell, whiteSpace: 'nowrap' }}
                    title={provenanceTooltip(log)}
                    onClick={() => openDetail(log, 'source')}
                  >
                    {(() => {
                      const chip = sourceChipProps(log.dataSource);
                      return (
                        <Chip
                          size='small'
                          label={chip.label}
                          sx={{
                            color: chip.color,
                            backgroundColor: chip.background,
                            fontWeight: 600,
                            fontSize: '0.72rem',
                          }}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...clickableCell,
                      fontSize: '0.8rem',
                      whiteSpace: 'nowrap',
                      fontFamily: 'monospace',
                    }}
                    title={provenanceTooltip(log)}
                    onClick={() => openDetail(log, 'vintage')}
                  >
                    {vintageText(log.dataVintage)}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...clickableCell,
                      fontSize: '0.85rem',
                      maxWidth: '400px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title='Click to read the full prompt'
                    onClick={() => openDetail(log, 'prompt')}
                  >
                    {log.prompt || '—'}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...clickableCell,
                      fontSize: '0.85rem',
                      maxWidth: '300px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title='Click to read the full args'
                    onClick={() => openDetail(log, 'args')}
                  >
                    {summarizeArgs(log.args)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth='md' fullWidth>
        <DialogTitle>{detail?.title}</DialogTitle>
        <DialogContent dividers>
          <Typography
            component='pre'
            sx={{
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              userSelect: 'text',
              fontFamily: detail?.monospace ? 'monospace' : 'inherit',
              fontSize: '0.9rem',
            }}
          >
            {detail?.text}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={copyDetail}>{copied ? 'Copied' : 'Copy'}</Button>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
